import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import {
  getAuthenticatorAppStatus,
  setupAuthenticatorApp,
  confirmAuthenticatorApp,
  disableAuthenticatorApp,
  regenerateBackupCodes,
} from "../../../../utils/authenticatorApp/authenticatorAppApi";
import { useSnackAlert } from "../../../../contexts/SnackAlertContext";

const NUMBER_OF_OTP_DIGITS = 6;

// Splits a base32 secret into 4-char groups ("ABCD EFGH ...") so the manual
// fallback is readable/typeable, same idea as how card numbers are grouped.
const formatSecret = (secret) => secret.match(/.{1,4}/g).join(" ");

// Keep in sync with accountSecurity.authenticatorApp in
// public/locales/en/common.json -- see the `translate` prop note in ./index.jsx.
const EN = {
  label: "Authenticator App",
  helper: "Get your two-step verification codes from an authenticator app.",
  setup: "Setup",
  disable: "Disable",
  configured: "Configured",
  notConfigured: "Not Configured",
  dialogTitle: "Set up authenticator app",
  scanInstructions:
    "Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.).",
  manualEntry:
    "Can't scan it? Enter the code below into your authenticator app:",
  code: "6-digit code",
  verify: "Verify",
  disableDialogTitle: "Disable authenticator app?",
  disableDialogBody:
    "You'll receive two-step verification codes by email instead.",
  setupError: "Failed to start authenticator app setup.",
  confirmSuccess: "Authenticator app configured.",
  confirmError: "Incorrect code. Please try again.",
  loadError: "Failed to load authenticator app status.",
  disableSuccess: "Authenticator app disabled.",
  disableError: "Failed to disable authenticator app.",
  backupCodesRemaining: ({ count } = {}) => `${count} backup codes remaining`,
  regenerateBackupCodes: "Regenerate backup codes",
  regenerateSuccess: "New backup codes generated.",
  regenerateError: "Failed to regenerate backup codes.",
  regenerateDialogTitle: "Regenerate backup codes?",
  regenerateDialogBody:
    "Your existing backup codes will stop working. Only the new codes below will let you sign in if you lose access to your authenticator app.",
  backupCodesDialogTitle: "Save your backup codes",
  backupCodesDialogBody:
    "Each code can be used once to sign in if you lose access to your authenticator app. Store them somewhere safe -- they won't be shown again.",
  backupCodesAcknowledge: "I've saved these codes somewhere safe",
  done: "Done",
};

export default function AuthenticatorApp({
  helperTextStyle,
  translate = true,
  onChange,
}) {
  const { t: i18nT } = useTranslation("common", {
    keyPrefix: "accountSecurity.authenticatorApp",
  });
  const { t: tCommonI18n } = useTranslation("common");
  const t = (key, options) => {
    if (translate) return i18nT(key, options);
    const entry = EN[key];
    return typeof entry === "function" ? entry(options) : entry;
  };
  const tCommon = (key) => (translate ? tCommonI18n(key) : "Cancel");
  const { setSnackAlert } = useSnackAlert();

  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(false);
  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  // Plaintext backup codes only ever exist transiently, straight from the
  // confirm/regenerate response -- never persisted or refetched.
  const [backupCodes, setBackupCodes] = useState(null);
  const [backupCodesSaved, setBackupCodesSaved] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { otp_via_app: otpViaApp, otp_backup_codes_remaining: remaining } =
        await getAuthenticatorAppStatus();
      setConfigured(otpViaApp);
      setBackupCodesRemaining(remaining || 0);
    } catch (e) {
      setSnackAlert(t("loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [setSnackAlert]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openSetupDialog = async () => {
    setSetupDialogOpen(true);
    setSettingUp(true);
    try {
      setSetupData(await setupAuthenticatorApp());
    } catch (e) {
      setSnackAlert(t("setupError"), "error");
      setSetupDialogOpen(false);
    } finally {
      setSettingUp(false);
    }
  };

  const closeSetupDialog = () => {
    setSetupDialogOpen(false);
    setSetupData(null);
    setCode("");
  };

  const handleVerify = async () => {
    setVerifying(true);
    try {
      const { backup_codes: codes } = await confirmAuthenticatorApp(code);
      setSnackAlert(t("confirmSuccess"), "success");
      setConfigured(true);
      setBackupCodesRemaining(codes?.length || 0);
      closeSetupDialog();
      setBackupCodesSaved(false);
      setBackupCodes(codes || null);
      onChange?.();
    } catch (e) {
      setSnackAlert(t("confirmError"), "error");
      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    setDisabling(true);
    try {
      await disableAuthenticatorApp();
      setSnackAlert(t("disableSuccess"), "success");
      setConfigured(false);
      setBackupCodesRemaining(0);
      setDisableDialogOpen(false);
      onChange?.();
    } catch (e) {
      setSnackAlert(t("disableError"), "error");
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { backup_codes: codes } = await regenerateBackupCodes();
      setSnackAlert(t("regenerateSuccess"), "success");
      setBackupCodesRemaining(codes?.length || 0);
      setRegenerateDialogOpen(false);
      setBackupCodesSaved(false);
      setBackupCodes(codes || null);
    } catch (e) {
      setSnackAlert(t("regenerateError"), "error");
    } finally {
      setRegenerating(false);
    }
  };

  const closeBackupCodesDialog = () => {
    setBackupCodes(null);
    setBackupCodesSaved(false);
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="body1">{t("label")}</Typography>
            {!loading && (
              <Chip
                label={configured ? t("configured") : t("notConfigured")}
                color={configured ? "success" : "warning"}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" sx={helperTextStyle}>
            {t("helper")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={() =>
            configured ? setDisableDialogOpen(true) : openSetupDialog()
          }
        >
          {configured ? t("disable") : t("setup")}
        </Button>
      </Stack>

      {configured && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1, alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="body2" sx={helperTextStyle}>
            {t("backupCodesRemaining", { count: backupCodesRemaining })}
          </Typography>
          {backupCodesRemaining === 0 && (
            <Button size="small" onClick={() => setRegenerateDialogOpen(true)}>
              {t("regenerateBackupCodes")}
            </Button>
          )}
        </Stack>
      )}

      <Dialog
        open={setupDialogOpen}
        onClose={closeSetupDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("dialogTitle")}</DialogTitle>
        <DialogContent>
          {settingUp || !setupData ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ alignItems: "center" }}>
              <DialogContentText>{t("scanInstructions")}</DialogContentText>
              <QRCodeSVG value={setupData.provisioning_uri} size={180} />
              <Typography variant="body2" sx={helperTextStyle}>
                {t("manualEntry")}
              </Typography>
              <Typography sx={{ fontFamily: "monospace" }}>
                {formatSecret(setupData.secret)}
              </Typography>
              <TextField
                autoFocus
                fullWidth
                margin="dense"
                label={t("code")}
                value={code}
                onChange={(e) =>
                  setCode(
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, NUMBER_OF_OTP_DIGITS)
                  )
                }
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    maxLength: NUMBER_OF_OTP_DIGITS,
                  },
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSetupDialog}>{tCommon("cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={
              !setupData || verifying || code.length !== NUMBER_OF_OTP_DIGITS
            }
          >
            {verifying ? <CircularProgress size={16} /> : t("verify")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("disableDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("disableDialogBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>
            {tCommon("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisable}
            disabled={disabling}
          >
            {disabling ? <CircularProgress size={16} /> : t("disable")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("regenerateDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("regenerateDialogBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateDialogOpen(false)}>
            {tCommon("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRegenerate}
            disabled={regenerating}
          >
            {regenerating ? (
              <CircularProgress size={16} />
            ) : (
              t("regenerateBackupCodes")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(backupCodes)}
        onClose={closeBackupCodesDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("backupCodesDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("backupCodesDialogBody")}</DialogContentText>
          <Box
            sx={{
              mt: 2,
              p: 2,
              fontFamily: "monospace",
              backgroundColor: (theme) => theme.palette.action.hover,
              borderRadius: 1,
            }}
          >
            {backupCodes?.map((backupCode) => (
              <Typography
                key={backupCode}
                sx={{ fontFamily: "monospace", my: 0.5 }}
              >
                {backupCode}
              </Typography>
            ))}
          </Box>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Checkbox
                checked={backupCodesSaved}
                onChange={(e) => setBackupCodesSaved(e.target.checked)}
              />
            }
            label={t("backupCodesAcknowledge")}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={closeBackupCodesDialog}
            disabled={!backupCodesSaved}
          >
            {t("done")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
